/**
 * Mapping DescriptifTheme → icône et variante de bloc (enumKey prioritaire, puis id).
 * Thèmes actifs : voir table descriptiftheme actif = true.
 */

/** Thème descriptif (id + enumKey optionnel) pour la résolution icône / variante */
export type DescriptifThemeRef = { id: number; enumKey?: string };

/**
 * Variante d'affichage du bloc (alignée sur apidae-detail-view-information-block).
 * Sémantique : secondary100, primary100 (primary 100%), primary10 (primary 10%).
 */
export type DescriptifThemeVariant =
  | "secondary100"
  | "grey"
  | "warning"
  | "primary100"
  | "primary10"
  | "green";

const DEFAULT_ICON = "bold/essentional_ui/info_circle";

/** enumKey → icône */
const ENUM_KEY_TO_ICON: Record<string, string> = {
  DESCRIPTIF_THEME_ALERTE_VISITEURS: "bold/essentional_ui/danger_triangle",
  DESCRIPTIF_THEME_AUCUN: "bold/essentional_ui/info_circle",
};

/** id → icône */
const ID_TO_ICON: Record<number, string> = {
  6301: "bold/shopping_ecommerce/cart_3", // Vente à emporter
  6303: "bold/essentional_ui/gift", // Bon cadeau
  6604: "bold/essentional_ui/danger_triangle", // Alerte visiteurs
  7174: "bold/messages_conversation/chat_round_like", // Coups de cœur / Incontournables
  7529: "bold/video_audio_sound/campaign", // Nouveauté 2026
  6724: "bold/video_audio_sound/campaign", // Offres promotionnelles
  5366: "bold/food_kitchen/chef_hat_minimalistic", // Menu spécial pour les fêtes
  5216: "bold/transport_parts_service/two_wheeler", // Services pour les motards
  6527: "bold/map_&_location/routing_2", // Topo/pas à pas
  7231: "bold/video_audio_sound/campaign", // Nouveauté 2025
  5161: "bold/sports/hiking", // Services pour les randonneurs
  6831: "bold/essentional_ui/info_circle", // Services pour les cavaliers
  5157: "bold/transport_parts_service/directions_car", // Conditions d'accès véhicules motorisés
  5352: "bold/food_kitchen/wineglass", // Label "Vignobles et Découvertes"
  5159: "bold/school/notebook_square", // Histoire, culture et patrimoine
  6375: "bold/essentional_ui/info_circle", // Informations sur ouverture anticipée (id 6 375)
  5154: "bold/essentional_ui/info_circle", // Conseils et suggestions
  6139: "bold/food_kitchen/restauration", // Restauration et ravitaillement
  6136: "bold/users/user", // Portrait
  5480: "bold/video_audio_sound/camera", // Conseils pour le photographe
  5417: "bold/astronomy/earth", // Tourisme durable
  5870: "bold/food_kitchen/chef_hat_heart", // Recette locale
  5160: "bold/home_furniture/child_hat", // Services pour les enfants
  6413: "bold/transport_parts_service/directions_boat", // Service pour les croisiéristes
  5162: "bold/sports/bicycling", // Services pour les cyclistes
};

/** Thèmes avec variante fixe (hors alternance secondary100/grey) */
const ENUM_KEY_TO_VARIANT: Record<string, DescriptifThemeVariant> = {
  DESCRIPTIF_THEME_ALERTE_VISITEURS: "warning",
  DESCRIPTIF_THEME_AUCUN: "primary10",
};

const ID_TO_VARIANT: Record<number, DescriptifThemeVariant> = {
  6604: "warning", // Alerte visiteurs
  5154: "primary10", // Conseils et suggestions
  5417: "green", // Tourisme durable
  6136: "primary100", // Portrait
  6527: "primary10", // Topo/pas à pas
};

/** Id du thème "Topo/pas à pas" (fallback si pas d'enumKey) */
const TOPO_PAS_A_PAS_THEME_ID = 6527;

/** Thèmes affichés dans chaque section (hors Topo qui est dans ItineraryAndTopo) */
export const DESCRIPTIF_THEME_IDS_BY_SECTION = {
  ALERTE_ET_CONSEILS: [6604, 5154] as const, // Alerte visiteurs, Conseils et suggestions
  SUSTAINABLE_AND_KIDS: [5417, 5160] as const, // Tourisme durable, Services enfants
  NEWS_AND_OFFERS: [7231, 7529, 6724] as const, // Nouveauté 2025, 2026, Offres promo
  CYCLISTS_AND_PUBLIC: [5162] as const, // Services cyclistes
  ITINERARY_AND_TOPO: [6527] as const, // Topo/pas à pas
} as const;

/** Ids de thèmes assignés à une section dédiée (pour calculer "autres") */
const ASSIGNED_THEME_IDS = new Set<number>([
  6604, 5154, 6527, 5417, 5160, 7231, 7529, 6724, 5162,
]);

/** Retourne true si le thème doit aller dans la section "Autres descriptifs thématisés". */
export function isOtherDescriptifTheme(themeId: number): boolean {
  return !ASSIGNED_THEME_IDS.has(themeId);
}
/** enumKey du thème "Topo/pas à pas" si défini en base */
const TOPO_PAS_A_PAS_ENUMKEY: string | undefined = undefined;

/**
 * Retourne true si le thème est "Topo/pas à pas" (résolution par enumKey ou id).
 */
export function isTopoPasAPasTheme(
  theme: DescriptifThemeRef | undefined,
): boolean {
  if (!theme) return false;
  if (
    TOPO_PAS_A_PAS_ENUMKEY &&
    theme.enumKey?.trim() === TOPO_PAS_A_PAS_ENUMKEY
  )
    return true;
  return typeof theme.id === "number" && theme.id === TOPO_PAS_A_PAS_THEME_ID;
}

/**
 * Retourne l'icône pour un thème : enumKey en priorité, puis id, sinon défaut.
 */
export function getDescriptifThemeIconName(
  theme: DescriptifThemeRef | undefined,
): string {
  if (!theme) return DEFAULT_ICON;
  const enumKey = theme.enumKey?.trim() ?? "";
  if (enumKey && ENUM_KEY_TO_ICON[enumKey]) return ENUM_KEY_TO_ICON[enumKey];
  const id = typeof theme.id === "number" ? theme.id : undefined;
  if (id !== undefined && ID_TO_ICON[id] !== undefined) return ID_TO_ICON[id];
  return DEFAULT_ICON;
}

/**
 * Retourne la variante de bloc pour un thème.
 * Variante fixe (warning, primary10, green, primary100) si définie, sinon alternance secondary100/grey selon index.
 * @param index index du bloc dans la liste (pour alternance white/grey), 0 si absent
 */
export function getDescriptifThemeVariant(
  theme: DescriptifThemeRef | undefined,
  index: number = 0,
): DescriptifThemeVariant {
  if (!theme) return "secondary100";
  const enumKey = theme.enumKey?.trim() ?? "";
  if (enumKey && ENUM_KEY_TO_VARIANT[enumKey])
    return ENUM_KEY_TO_VARIANT[enumKey];
  const id = typeof theme.id === "number" ? theme.id : undefined;
  if (id !== undefined && ID_TO_VARIANT[id] !== undefined)
    return ID_TO_VARIANT[id];
  return index % 2 === 1 ? "grey" : "secondary100";
}
